import { Component } from '@theme/component';
import { debounce, onAnimationEnd } from '@theme/utilities';
import { MegaMenuHoverEvent, ThemeEvents } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/** Swym injects the wishlist modal under body (#swym-plugin). Used to keep dropdown open when it is open. */
function isSwymWishlistModalOpen() {
  const el = document.querySelector('.swym-modal.swym-add-wishlist-modal, .swym-add-wishlist-modal');
  if (!(el instanceof HTMLElement)) return false;
  if (el.offsetParent !== null) return true;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * A custom element that manages a search dropdown menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class SearchMenu extends Component {
  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #abortController = new AbortController();
  /**
   * @type {AbortController | null}
   */
  #submenuAbortController = null;
  /**
   * @type {MutationObserver | null}
   */
  #submenuObserver = null;
  /**
   * @type {number | null}
   */
  #scrollY = null;
  /**
   * @type {boolean}
   */
  #isClosing = false;
  /**
   * @type {HTMLElement | null}
   */
  #cachedSubmenu = null;
  /**
   * @type {number | null}
   */
  #lastPointerEnterTime = null;
  /**
   * @type {AbortController | null}
   */
  #transitionAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.megaMenuHover, this.#handleMegaMenuHover, { signal });
    document.addEventListener('keydown', this.#handleKeyDown, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#submenuObserver?.disconnect();
    this.#transitionAbortController?.abort();
    this.#unlockBodyScroll();
  }

  /**
   * Get the active submenu
   * @returns {HTMLElement | null}
   */
  #getActiveSubmenu() {
    if (!this.#state.activeItem) return null;
    return findSubmenu(this.#state.activeItem);
  }

  /**
   * Get the combined height of header and announcement bar
   * @returns {number}
   */
  #getTopOffsetHeight() {
    const headerHeight = getComputedStyle(document.body).getPropertyValue('--header-height');
    let header = 0;
    if (headerHeight) {
      header = Number.parseFloat(headerHeight) || 0;
    } else {
      const headerElement = document.querySelector('header-component');
      header = headerElement instanceof HTMLElement ? headerElement.offsetHeight : 0;
    }

    const announcementBar = document.querySelector('.announcement-bar');
    const announcement = announcementBar instanceof HTMLElement ? announcementBar.offsetHeight : 0;

    return header + announcement;
  }

  /**
   * Check if an element is within the search menu or its submenu
   * @param {Element | null} element
   * @returns {boolean}
   */
  #isWithinSearchMenu(element) {
    if (!element) return false;
    return this.contains(element) || this.#cachedSubmenu?.contains(element) || false;
  }

  /**
   * Whether the submenu is hovered
   * @returns {boolean}
   */
  get submenuHovered() {
    const submenu = this.#getActiveSubmenu();
    return submenu?.matches(':hover') ?? false;
  }

  /**
   * Handle mega menu hover event from header-menu
   * @param {Event} event
   */
  #handleMegaMenuHover = (event) => {
    if (event.target !== this && this.#state.activeItem) {
      this.#deactivate();
    }
  };

  /**
   * Handle keyboard events
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    // Early return if not Escape key or dropdown not open
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    const activeElement = document.activeElement;
    const isWithinSearchMenu = this.#isWithinSearchMenu(activeElement);

    if (isWithinSearchMenu || !activeElement || activeElement === document.body) {
      event.preventDefault();
      event.stopPropagation();
      this.#isClosing = true;
      this.#deactivate(undefined, true); // Force close even if hovered
      // Return focus to the button after animation frame to prevent focus event from reopening
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const button = this.querySelector('[ref="menuitem"]');
          if (button instanceof HTMLElement) {
            button.focus();
          }
          this.#isClosing = false;
        });
      });
    }
  };

  /**
   * Handle window resize events
   * Close dropdown if screen width is less than 750px
   */
  #handleResize = () => {
    if (window.innerWidth < 750 && this.#state.activeItem) {
      this.#deactivate(undefined, true);
    }
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? Number.parseInt(value, 10) : 0;
  }

  /**
   * Toggle the search dropdown (for click events)
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Reset pointer enter time on any click to ensure clean state
    this.#lastPointerEnterTime = null;

    if (this.#state.activeItem) {
      // If already active, close it (force close even if hovered)
      this.#deactivate(undefined, true);
    } else {
      // If not active, open it
      // On touch devices, directly activate without going through pointerenter check
      // since click events are MouseEvent, not PointerEvent
      this.#debouncedDeactivate.cancel();
      this.#debouncedActivateHandler.cancel();
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Don't activate if we're in the process of closing
    if (this.#isClosing) return;

    // On touch devices, ignore pointerenter events to prevent hover/click conflicts
    // Only allow focus events or explicit clicks
    if (event instanceof PointerEvent) {
      // Ignore non-mouse pointer events (touch, pen) to prevent hover/click conflicts
      if (event.pointerType !== 'mouse') {
        this.#lastPointerEnterTime = Date.now();
        return;
      }
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Lock body scroll
    this.#lockBodyScroll();

    const submenu = findSubmenu(item);
    this.#cachedSubmenu = submenu;

    if (submenu) {
      if (this.#submenuAbortController) {
        this.#submenuAbortController.abort();
      }
      this.#submenuAbortController = new AbortController();
      const { signal } = this.#submenuAbortController;
      submenu.addEventListener('pointerleave', () => this.#debouncedDeactivate(), { signal });
      submenu.addEventListener(
        'click',
        (e) => {
          if (e.target instanceof Element && e.target.closest('.resource-card__wishlist-button')) {
            this.#debouncedDeactivate.cancel();
          }
        },
        { capture: true, signal }
      );

      const submenuInner = submenu.querySelector('.menu-list__submenu-inner');

      const updateHeight = () => {
        let submenuHeight = 0;
        if (submenuInner instanceof HTMLElement) {
          submenuHeight = Math.max(submenuInner.scrollHeight, submenuInner.offsetHeight);
        } else {
          submenuHeight = submenu.scrollHeight || submenu.offsetHeight;
        }

        const topOffsetHeight = this.#getTopOffsetHeight();
        const maxHeight = window.innerHeight - topOffsetHeight;

        const finalHeight = Math.min(submenuHeight, maxHeight);
        this.style.setProperty('--submenu-height', `${finalHeight}px`);
        this.style.setProperty('--submenu-opacity', '1');
      };

      submenu.removeAttribute('hidden');
      requestAnimationFrame(() => {
        updateHeight();
        requestAnimationFrame(updateHeight);

        // Focus search input when search dropdown opens
        const searchInput = submenu.querySelector('input[type="search"]');
        if (searchInput instanceof HTMLInputElement) {
          searchInput.focus();
        }
      });

      // Observe submenu content changes to update height when content re-renders
      if (submenuInner instanceof HTMLElement) {
        this.#submenuObserver?.disconnect();
        this.#submenuObserver = new MutationObserver(() => {
          requestAnimationFrame(updateHeight);
        });

        this.#submenuObserver.observe(submenuInner, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class'],
        });
      }
    } else {
      this.#cachedSubmenu = null;
      this.style.setProperty('--submenu-height', '0px');
      this.style.setProperty('--submenu-opacity', '1');
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    // On touch devices, ignore pointerleave events to prevent hover/click conflicts
    if (event instanceof PointerEvent && event.pointerType !== 'mouse') {
      return;
    }

    // If this is a blur event, don't close when focus moves to search menu or to Swym modal (injected in body)
    if (event instanceof FocusEvent && event.type === 'blur') {
      const relatedTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
      const activeElement = document.activeElement instanceof Element ? document.activeElement : null;

      const isFocusWithinSearchMenu =
        this.#isWithinSearchMenu(relatedTarget) || this.#isWithinSearchMenu(activeElement);
      const isFocusInSwymModal = [
        relatedTarget?.closest('.swym-modal, .swym-add-wishlist-modal'),
        activeElement?.closest('.swym-modal, .swym-add-wishlist-modal'),
      ].some(Boolean);

      if (isFocusWithinSearchMenu || isFocusInSwymModal) return;

      if (relatedTarget === null) {
        requestAnimationFrame(() => {
          const el = document.activeElement instanceof Element ? document.activeElement : null;
          if (el?.closest('.swym-modal, .swym-add-wishlist-modal')) return;
          const item = findMenuItem(event.target);
          if (item === this.#state.activeItem) this.#debouncedDeactivate();
        });
        return;
      }
    }

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force=false] - Force deactivation even if submenu is hovered
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item !== this.#state.activeItem) return;
    if (!force && this.submenuHovered) return;
    if (!force && isSwymWishlistModalOpen()) return;

    if (this.#submenuAbortController) {
      this.#submenuAbortController.abort();
      this.#submenuAbortController = null;
    }

    this.#submenuObserver?.disconnect();
    this.#submenuObserver = null;

    // Clean up previous transition listener
    if (this.#transitionAbortController) {
      this.#transitionAbortController.abort();
    }

    const submenu = findSubmenu(item);

    this.#cachedSubmenu = null;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    // Unlock body scroll
    this.#unlockBodyScroll();

    // Reset touch-related state
    this.#lastPointerEnterTime = null;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    // Wait for transition to complete before hiding submenu
    if (submenu) {
      this.#transitionAbortController = new AbortController();
      const { signal } = this.#transitionAbortController;
      let transitionCompleted = false;

      const cleanup = () => {
        if (transitionCompleted) return;
        transitionCompleted = true;
        item.removeAttribute('data-animating');
        submenu.setAttribute('hidden', '');
        submenu.removeEventListener('transitionend', handleTransitionEnd);
      };

      /**
       * @param {TransitionEvent} event
       */
      const handleTransitionEnd = (event) => {
        if (event.propertyName === 'clip-path') {
          cleanup();
        }
      };

      submenu.addEventListener('transitionend', handleTransitionEnd);
      
      // Fallback: handles edge case where window resize interrupts transition,
      // preventing transitionend from firing. Only runs cleanup if transitionend
      // hasn't already set transitionCompleted = true.
      requestAnimationFrame(() => {
        if (!transitionCompleted && !signal.aborted) {
          cleanup();
        }
      });
      
      // Cleanup if aborted
      signal.addEventListener('abort', () => {
        cleanup();
      });
    } else {
      // Fallback: remove immediately if no submenu
      item.removeAttribute('data-animating');
    }
  };

  /**
   * Lock body scroll when dropdown is open
   */
  #lockBodyScroll() {
    if (this.#scrollY !== null) return; // Already locked

    this.#scrollY = window.scrollY;
    document.body.style.overflowY = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollY}px`;
    document.body.style.width = '100%';
  }

  /**
   * Unlock body scroll when dropdown is closed
   */
  #unlockBodyScroll() {
    if (this.#scrollY === null) return; // Not locked

    const scrollY = this.#scrollY;
    this.#scrollY = null;

    document.body.style.overflowY = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    window.scrollTo({ top: scrollY, behavior: 'instant' });
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);
}

if (!customElements.get('search-menu')) {
  customElements.define('search-menu', SearchMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const menuitem = element.closest('[ref="menuitem"]') || element.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

